import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api, tokenStore } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const WS_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
const RtCtx = createContext(null);

export const RealtimeProvider = ({ children }) => {
  const { user } = useAuth();
  const listeners = useRef(new Set());
  const [counts, setCounts] = useState({});
  const [online, setOnline] = useState(0);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!user) return undefined;
    let ws;
    let stopped = false;
    let retry = 0;
    let pingId;
    const open = () => {
      const tok = tokenStore.get()?.access_token;
      if (!tok || stopped) return;
      ws = new WebSocket(`${WS_BASE}/api/ws?token=${encodeURIComponent(tok)}`);
      ws.onopen = () => {
        retry = 0;
        setConnected(true);
        pingId = setInterval(() => ws.readyState === 1 && ws.send("ping"), 25000);
      };
      ws.onmessage = (ev) => {
        const m = JSON.parse(ev.data);
        if (m.type === "queue_counts") {
          setCounts(m.counts || {});
          setOnline(m.online || 0);
        }
        listeners.current.forEach((fn) => fn(m));
      };
      ws.onclose = async (ev) => {
        setConnected(false);
        clearInterval(pingId);
        if (stopped) return;
        if (ev.code === 4401) await api.get("/auth/me").catch(() => {});
        retry += 1;
        setTimeout(open, Math.min(1000 * 2 ** retry, 10000));
      };
    };
    open();
    return () => {
      stopped = true;
      clearInterval(pingId);
      if (ws) ws.close();
    };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const subscribe = useCallback((fn) => {
    listeners.current.add(fn);
    return () => listeners.current.delete(fn);
  }, []);

  return <RtCtx.Provider value={{ counts, online, connected, subscribe }}>{children}</RtCtx.Provider>;
};

export const useRealtime = () => useContext(RtCtx);

export const useRealtimeEvent = (handler) => {
  const { subscribe } = useRealtime();
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => subscribe((m) => ref.current(m)), [subscribe]);
};
